type UnknownRecord = Record<string, unknown>;

export interface PublishResultSummary {
	postUrl?: string;
	postId?: string;
	requestId?: string;
	jobId?: string;
	status?: string;
	isAsync?: boolean;
	success?: boolean;
	reason?: string;
	message?: string;
	scheduledCancel?: boolean;
	scheduledAt?: string;
}

export interface AnalyticsMetrics {
	likes: number;
	comments: number;
	shares: number;
	views: number;
	quotes: number;
}

const isRecord = (value: unknown): value is UnknownRecord =>
	typeof value === 'object' && value !== null;

const asString = (value: unknown): string | undefined =>
	typeof value === 'string' && value.length > 0 ? value : undefined;

const asNumber = (value: unknown): number =>
	typeof value === 'number' && Number.isFinite(value) ? value : 0;

const asBoolean = (value: unknown): boolean | undefined =>
	typeof value === 'boolean' ? value : undefined;

const normalizePlatformKey = (value: unknown): string | undefined => {
	const platform = asString(value);
	if (!platform) return undefined;

	const normalized = platform.toLowerCase().replace(/[\s/_-]+/g, '');
	const aliases: Record<string, string> = {
		twitter: 'x',
		twitterx: 'x',
		xtwitter: 'x',
		googlebusinessprofile: 'googlebusiness',
	};
	return aliases[normalized] || normalized;
};

const firstRecord = (value: unknown): UnknownRecord | undefined => {
	if (Array.isArray(value)) {
		return value.find(isRecord);
	}
	return isRecord(value) ? value : undefined;
};

const firstMetricRecord = (value: unknown): UnknownRecord | undefined => {
	if (Array.isArray(value)) {
		return value.find(isRecord);
	}
	if (!isRecord(value)) return undefined;
	if (
		'post_metrics' in value ||
		'metrics' in value ||
		'latest' in value ||
		'likes' in value ||
		'views' in value ||
		'impressions' in value
	) {
		return value;
	}
	return Object.values(value).find(isRecord);
};

const getNestedRecord = (
	value: UnknownRecord | undefined,
	key: string
): UnknownRecord | undefined => {
	if (!value) return undefined;
	return firstRecord(value[key]);
};

const platformMatches = (value: unknown, preferredPlatform?: string): boolean =>
	!!preferredPlatform &&
	normalizePlatformKey(value) === normalizePlatformKey(preferredPlatform);

const getPlatformResult = (
	results: UnknownRecord | undefined,
	preferredPlatform?: string
): UnknownRecord | undefined => {
	if (!results || !preferredPlatform) return undefined;
	const entry = Object.entries(results).find(([platform]) =>
		platformMatches(platform, preferredPlatform)
	);
	return entry ? firstRecord(entry[1]) : undefined;
};

export function extractPublishResultSummary(
	response: unknown,
	preferredPlatform?: string
): PublishResultSummary {
	if (!isRecord(response)) return {};

	const posts = Array.isArray(response.posts) ? response.posts : [];
	const legacyPost =
		posts.find(
			(post) =>
				isRecord(post) &&
				(platformMatches(post.platform, preferredPlatform) ||
					platformMatches(post.network, preferredPlatform) ||
					platformMatches(post.service, preferredPlatform))
		) || firstRecord(posts);
	const legacyPostIds = Array.isArray(legacyPost?.postIds)
		? legacyPost?.postIds
		: [];
	const legacyPostId = firstRecord(legacyPostIds);

	const data = getNestedRecord(response, 'data');
	const result = getNestedRecord(response, 'result');
	const results = isRecord(response.results) ? response.results : undefined;
	const platformResult =
		getPlatformResult(results, preferredPlatform) ||
		firstRecord(results ? Object.values(results) : undefined);

	const source =
		platformResult ||
		result ||
		data ||
		legacyPostId ||
		legacyPost ||
		response;

	const summary: PublishResultSummary = {
		postUrl:
			asString(source.postUrl) ||
			asString(source.url) ||
			asString(source.post_url) ||
			asString(result?.url) ||
			asString(legacyPostId?.postUrl),
		postId:
			asString(source.postId) ||
			asString(source.post_id) ||
			asString(source.publish_id) ||
			asString(source.id) ||
			asString(legacyPostId?.id),
		requestId:
			asString(response.request_id) ||
			asString(response.requestId) ||
			asString(data?.request_id) ||
			asString(data?.requestId) ||
			asString(source.request_id) ||
			asString(source.requestId),
		jobId:
			asString(response.job_id) ||
			asString(response.jobId) ||
			asString(data?.job_id) ||
			asString(data?.jobId) ||
			asString(source.job_id) ||
			asString(source.jobId),
		status:
			asString(response.status) ||
			asString(data?.status) ||
			asString(source.status),
		isAsync:
			response.async_upload === true ||
			response.asyncUpload === true ||
			data?.async_upload === true ||
			data?.asyncUpload === true,
	};

	const success =
		asBoolean(response.success) ??
		asBoolean(data?.success) ??
		asBoolean(source.success);
	if (success !== undefined) summary.success = success;

	const reason =
		asString(response.reason) ||
		asString(data?.reason) ||
		asString(source.reason);
	if (reason) summary.reason = reason;

	const message =
		asString(response.message) ||
		asString(data?.message) ||
		asString(source.message);
	if (message) summary.message = message;

	const scheduledCancel =
		response.scheduledCancel === true ||
		data?.scheduledCancel === true ||
		source.scheduledCancel === true;
	if (scheduledCancel) summary.scheduledCancel = true;

	const scheduledAt =
		asString(response.scheduled_at) ||
		asString(response.scheduledAt) ||
		asString(response.scheduled_date) ||
		asString(response.scheduledDate) ||
		asString(data?.scheduled_at) ||
		asString(data?.scheduledAt) ||
		asString(data?.scheduled_date) ||
		asString(data?.scheduledDate) ||
		asString(source.scheduled_at) ||
		asString(source.scheduledAt) ||
		asString(source.scheduled_date) ||
		asString(source.scheduledDate);
	if (scheduledAt) summary.scheduledAt = scheduledAt;

	return summary;
}

export function extractAnalyticsMetrics(response: unknown): AnalyticsMetrics {
	const root = isRecord(response) ? response : {};
	const platformPayload =
		firstMetricRecord(root.platforms) ||
		firstMetricRecord(getNestedRecord(root, 'data')?.platforms) ||
		firstMetricRecord(root.results) ||
		firstMetricRecord(getNestedRecord(root, 'data')?.results);
	const firstData = firstRecord(root.data);
	const metrics =
		getNestedRecord(root, 'post_metrics') ||
		getNestedRecord(root, 'metrics') ||
		getNestedRecord(platformPayload, 'post_metrics') ||
		getNestedRecord(platformPayload, 'metrics');
	const result = getNestedRecord(root, 'result');
	const latest =
		getNestedRecord(root, 'latest') ||
		getNestedRecord(platformPayload, 'latest');
	const source = metrics || latest || result || firstData || platformPayload || root;

	return {
		likes: asNumber(source.likes),
		comments: asNumber(source.comments),
		shares:
			asNumber(source.shares) ||
			asNumber(source.retweets) ||
			asNumber(source.reposts),
		views:
			asNumber(source.views) ||
			asNumber(source.impressions) ||
			asNumber(source.reach),
		quotes: asNumber(source.quotes),
	};
}
